// The survey's Responses page (spec §2): the list on its own — one filter
// bar, the table, paging, sorting and one Export of exactly what is listed.
// Same URL keys as Results, so a segment means the same on both pages.

import {
    Badge,
    Button,
    buttonVariants,
    Card,
    Confirm,
    DataTable,
    type DataTableColumn,
    type FilterValue,
    NULL_FILTER_VALUE,
    SearchBar,
    toast,
    useKoreSearch,
} from "@pej/kore/components";
import { Link } from "@tanstack/react-router";
import { PanelRight, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { usePlacements } from "@/api/placements";
import {
    type ResponseSearchQueryField,
    type ResponseSortField,
    useDeleteResponse,
    useResponseSearch,
} from "@/api/responses";
import { useSecurity } from "@/api/security";
import type { PlacementRow, ResponseRow, StepRow, Survey } from "@/api/types";
import { toastMutationError } from "@/components/error-toast";
import { TABLE_LABELS, tableError } from "@/components/page-states";
import { type RowActionDescriptor, RowActionsMenu } from "@/components/row-actions-menu";
import { SectionPageShell } from "@/components/section-page/section-page-shell";
import { metricStep } from "@/features/dashboard/stats-mapping";
import { usePageSearch } from "@/features/search/page-search";
import { sortableHeader } from "@/features/search/sortable-header";
import { useCursorStack } from "@/features/search/use-cursor-stack";
import { useTimeChipRouter } from "@/features/time/time-chip";
import { answerFormatter, formatSubmittedAt, ScoreBadge } from "./response-format";
import { ResponseSheet } from "./response-sheet";
import {
    exportLabel,
    filtersForWire,
    isTimeFilter,
    narrowsBeyondTime,
    optionByScore,
    pickTime,
    type RawFilters,
    RESPONSE_DEFAULT_ORDER,
    RESPONSE_PAGE_SIZE_OPTIONS,
    RESPONSE_QUERY_FIELDS,
    RESPONSE_SORT_FIELDS,
    responsesManifest,
    segmentSearch,
    sortedSteps,
    stepLabel,
} from "./responses-filters";
import { useExport } from "./use-export";

export interface ResponsesPageProps {
    survey: Survey;
    workspaceId: string;
}

// One empty list for every render without placements: a fresh `[]` would
// rebuild the manifest, and with it kore's request, every render.
const EMPTY_PLACEMENTS: PlacementRow[] = [];

/** kore's null option for Respondent reads "Anonymous", in the picker and on the chip. */
function renderFilterValue(filterParam: string, value: FilterValue) {
    return filterParam === "respondentRef" && value.value === NULL_FILTER_VALUE ? "Anonymous" : undefined;
}

/** One step's answer in its cell: the shared wording, text truncated with
 *  the full text on hover (the response panel shows it whole). */
function stepColumn(step: StepRow, index: number): DataTableColumn<ResponseRow> {
    const format = answerFormatter(step);
    return {
        key: `step-${step.id}`,
        header: stepLabel(index, step),
        cell: (row) => {
            const text = format(row.answers.find((a) => a.stepId === step.id));
            if (text === null) return null;
            return step.kind === "text" ? (
                <span title={text} className="line-clamp-1">
                    {text}
                </span>
            ) : (
                text
            );
        },
    };
}

export function ResponsesPage({ survey, workspaceId }: ResponsesPageProps) {
    const page = usePageSearch<ResponseSortField, ResponseSearchQueryField>({
        sortFields: RESPONSE_SORT_FIELDS,
        defaultOrder: RESPONSE_DEFAULT_ORDER,
        queryFields: RESPONSE_QUERY_FIELDS,
        pageSizeOptions: RESPONSE_PAGE_SIZE_OPTIONS,
    });
    const router = useTimeChipRouter(page.rawSearch, page.onSearchPatch);
    const placements = usePlacements(survey.id).data ?? EMPTY_PLACEMENTS;
    const manifest = useMemo(() => responsesManifest(survey, placements), [survey, placements]);
    const search = useKoreSearch<RawFilters>({
        manifest,
        router,
        orderBy: page.orderBy,
        onOrderByChange: page.onOrderByChange,
        query: page.query,
        onQueryChange: page.setQuery,
        renderFilterValue,
    });
    // Without a key in the workspace nothing could have been signed, so the
    // Verified column would say nothing and is left out.
    const canVerify = (useSecurity(workspaceId).data?.keys.length ?? 0) > 0;
    const predicate = page.predicate(filtersForWire(search.rq?.filters));
    const cursor = useCursorStack({ request: predicate });
    const responsesQuery = useResponseSearch(
        survey.id,
        search.ready ? { ...predicate, pageToken: cursor.pageToken } : null,
    );
    // The "of {total}" in the Export label (spec §5): the same time filter
    // with nothing else, one row — asked only when something else narrows the list.
    const narrowed = narrowsBeyondTime(predicate.filters) || Boolean(predicate.query);
    const totalQuery = useResponseSearch(
        survey.id,
        search.ready && narrowed ? { filters: pickTime(predicate.filters), pageSize: 1 } : null,
    );
    // Clears what narrows beyond the time chip — the other chips and the
    // search — and keeps the period, which says when, not what.
    function clearFilters() {
        page.setQuery("");
        search.barProps.onFiltersChange(search.filters.filter((chip) => isTimeFilter(chip.filterParam)));
    }
    // A survey nobody has answered yet says so, whatever the filters (the
    // default time chip would otherwise point at the wrong cause).
    const emptyState =
        survey.responseCount === 0
            ? { headline: "No responses yet." }
            : narrowed
              ? {
                    headline: "No responses match these filters.",
                    body: "Try a different search or clear the filters.",
                    action: (
                        <Button variant="outline" size="sm" onClick={clearFilters}>
                            Clear filters
                        </Button>
                    ),
                }
              : Object.keys(pickTime(predicate.filters)).length > 0
                ? { headline: "No responses in this period.", body: "Remove the time filter to see every response." }
                : { headline: "No responses yet." };
    const toResults = useMemo(
        () => segmentSearch(page.rawSearch, survey, placements),
        [page.rawSearch, survey, placements],
    );
    const params = { workspaceId, surveyId: survey.id };
    const deleteResponse = useDeleteResponse(workspaceId, survey.id);
    const [deleteTarget, setDeleteTarget] = useState<ResponseRow | null>(null);
    const [openRow, setOpenRow] = useState<ResponseRow | null>(null);
    const rowActions = useMemo(
        (): RowActionDescriptor<ResponseRow>[] => [
            { id: "open", label: "Open", icon: PanelRight, onClick: (row) => setOpenRow(row) },
            {
                id: "delete",
                label: "Delete response",
                icon: Trash2,
                section: "danger",
                onClick: (row) => setDeleteTarget(row),
            },
        ],
        [],
    );
    const tableExport = useExport(survey.id);

    const columns = useMemo((): DataTableColumn<ResponseRow>[] => {
        const scored = metricStep(survey);
        const scoreMeta = optionByScore(scored);
        return [
            {
                key: "SubmittedAt",
                header: sortableHeader("Submitted", true),
                sortable: true,
                cell: (row) => formatSubmittedAt(row.submittedAt),
            },
            { key: "placement", header: "Placement", cell: (row) => row.placementName },
            { key: "location", header: "Location", cell: (row) => row.locationRef ?? "" },
            ...(scored
                ? [
                      {
                          key: "Score",
                          header: sortableHeader("Score", true),
                          sortable: true,
                          cell: (row: ResponseRow) => <ScoreBadge score={row.score} meta={scoreMeta} />,
                      },
                  ]
                : []),
            // The score step's answer is the Score column; the other steps
            // keep their numbers, counted over every step.
            ...sortedSteps(survey).flatMap((step, index) => (step.id === scored?.id ? [] : [stepColumn(step, index)])),
            ...(canVerify
                ? [
                      {
                          key: "verified",
                          header: "Verified",
                          cell: (row: ResponseRow) =>
                              row.contextTrusted ? (
                                  <span role="img" aria-label="Verified" className="text-kore-success-text">
                                      ✓
                                  </span>
                              ) : null,
                      },
                  ]
                : []),
            {
                key: "actions",
                header: "",
                align: "right",
                cell: (row) => (
                    <RowActionsMenu
                        actions={rowActions}
                        entity={row}
                        label={`Actions for the response of ${formatSubmittedAt(row.submittedAt)}`}
                    />
                ),
            },
        ];
    }, [survey, canVerify, rowActions]);

    async function handleDelete() {
        if (!deleteTarget) return;
        try {
            await deleteResponse.mutateAsync(deleteTarget.id);
            toast.success("Response deleted");
            setDeleteTarget(null);
        } catch (err) {
            toastMutationError("Delete failed", err);
        }
    }

    return (
        <SectionPageShell
            backLabel={survey.name}
            backLink={<Link to="/w/$workspaceId/surveys/$surveyId" params={params} />}
            title="Responses"
            badges={
                <>
                    <Badge tone={survey.active ? "success" : "neutral"}>{survey.active ? "Active" : "Inactive"}</Badge>
                    {survey.steps.some((step) => step.isMetric) && <Badge tone="accent">Scored</Badge>}
                </>
            }
            actions={
                <>
                    <Link
                        to="/w/$workspaceId/surveys/$surveyId/results"
                        params={params}
                        search={toResults}
                        className={buttonVariants({ size: "sm" })}
                    >
                        View results
                    </Link>
                    <Link
                        to="/w/$workspaceId/surveys/$surveyId"
                        params={params}
                        className={buttonVariants({ variant: "outline", size: "sm" })}
                    >
                        Edit survey
                    </Link>
                </>
            }
        >
            {/* kore's Card, not SectionCard: the page title already names the list. */}
            <Card className="flex flex-col gap-3 p-4">
                <div className="flex flex-wrap items-start gap-3">
                    {/* At least 20rem, as on Results: on a narrow column the
                        search wraps rather than shrinks to a few letters. */}
                    <div className="min-w-0 flex-1 basis-80">
                        <SearchBar {...search.barProps} size="compact" />
                    </div>
                    <Link
                        to="/w/$workspaceId/surveys/$surveyId/results"
                        params={params}
                        search={toResults}
                        // Button height on the top row, level with Export: centered
                        // against the search it drifts as filter chips grow it.
                        className="inline-flex h-8 shrink-0 items-center text-sm text-kore-accent-text hover:underline"
                    >
                        See results for these filters ›
                    </Link>
                    <Button
                        variant="outline"
                        size="sm"
                        // Stays at the right edge when it wraps to a row of its own.
                        className="ml-auto"
                        // Until the search is ready the filters aren't the table's yet.
                        disabled={!search.ready || tableExport.exporting}
                        onClick={() =>
                            void tableExport.run({
                                filters: predicate.filters,
                                query: predicate.query,
                                orderBy: predicate.orderBy,
                            })
                        }
                    >
                        {tableExport.exporting
                            ? "Exporting…"
                            : exportLabel(responsesQuery.data?.totalCount, totalQuery.data?.totalCount, narrowed)}
                    </Button>
                </div>
                <DataTable
                    density="compact"
                    caption="Responses"
                    columns={columns}
                    rows={responsesQuery.data?.items ?? []}
                    rowKey={(row) => row.id}
                    onRowClick={(row) => setOpenRow(row)}
                    state={responsesQuery.isError ? "error" : responsesQuery.isPending ? "loading" : undefined}
                    error={tableError(responsesQuery.error, () => void responsesQuery.refetch())}
                    labels={TABLE_LABELS}
                    {...search.tableSortProps}
                    pagination={{
                        ...cursor.pager(responsesQuery.data?.nextPageToken ?? null),
                        pageSize: page.pageSize,
                        totalCount: responsesQuery.data?.totalCount ?? 0,
                        pageSizeOptions: [...RESPONSE_PAGE_SIZE_OPTIONS],
                        onPageSizeChange: page.setPageSize,
                        entityLabel: "response",
                    }}
                    empty={emptyState}
                />
                <ResponseSheet
                    survey={survey}
                    row={openRow}
                    canVerify={canVerify}
                    onClose={() => setOpenRow(null)}
                    onDelete={(row) => {
                        setOpenRow(null);
                        setDeleteTarget(row);
                    }}
                />
                <Confirm
                    open={deleteTarget !== null}
                    onOpenChange={(open) => {
                        if (!open && !deleteResponse.isPending) setDeleteTarget(null);
                    }}
                    title="Delete this response?"
                    description="This permanently removes the response and all its answers. This cannot be undone."
                    variant="destructive"
                    primaryLabel={deleteResponse.isPending ? "Deleting…" : "Delete"}
                    confirming={deleteResponse.isPending}
                    onConfirm={handleDelete}
                />
            </Card>
        </SectionPageShell>
    );
}
